import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  RowDataUpdatedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { MultiProject, MultiProjectArea, MultiProjectAreaStatistic } from "fond/types";
import { formatNumber } from "fond/utils/number";
import { AgGrid } from "fond/widgets";

import { rankingColumns } from "../AssessPanel/rankingColumns";

const CompletedIndicatorRenderer = () => {
  return (
    <Box sx={{ display: "flex", height: "var(--ag-internal-content-line-height)" }}>
      <Box className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
        <Box className="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ag-checked ag-disabled" />
      </Box>
    </Box>
  );
};

interface SubareaListProps {
  /**
   * Flag indicating if the list should be placed into loading mode.
   */
  loading?: boolean;
  /**
   * The multi project to display the areas of.
   */
  multiProject: MultiProject;
  /**
   * The multi project area statistics.
   */
  areaStatistics?: MultiProjectAreaStatistic[];
  /**
   * The currently selected area ids.
   */
  selectedIds: string[];
  /**
   * A collection of area ids that make the match rows within the grid appear selected,
   * while being unselectable.
   */
  previouslySelectedIds: string[];
  /**
   * Callback event fired when row selection data changes.
   */
  onSelectionChanged(): void;
}

const SubareaList = forwardRef<AgGridReact, SubareaListProps>(
  ({ loading = false, multiProject, areaStatistics, onSelectionChanged, previouslySelectedIds, selectedIds }: SubareaListProps, ref) => {
    const gridApi = useRef<GridApi | null>(null);
    const previouslySelectedIdsRef = useRef<string[]>(previouslySelectedIds);

    // We use a ref to store the previously selected ids so that we can access the latest value in the row selection callbacks without needing to re-render the component.
    useEffect(() => {
      previouslySelectedIdsRef.current = previouslySelectedIds;
    }, [previouslySelectedIds]);

    const rowSelection: GridOptions["rowSelection"] = useMemo(
      () => ({
        mode: "multiRow",
        headerCheckbox: true,

        // Previously selected rows are not selectable (won't be affected by "Select All")
        isRowSelectable: (params) => {
          const id = params.data?.ID;
          return id ? !previouslySelectedIdsRef.current.includes(id) : true;
        },

        // Hide checkboxes for previously selected items (they'll show an icon instead)
        checkboxes: (params) => {
          const id = params.data?.ID;
          return id ? !previouslySelectedIdsRef.current.includes(id) : true;
        },
      }),
      []
    );

    useEffect(() => {
      gridApi.current?.setGridOption("rowSelection", rowSelection);
    }, [rowSelection]);

    // When previouslySelectedIds changes, refresh cells to update the cell renderers
    useEffect(() => {
      if (gridApi.current && previouslySelectedIds.length > 0) {
        gridApi.current.refreshCells({ force: true });
      }
    }, [previouslySelectedIds]);

    const gridOptions: GridOptions = useMemo(
      () => ({
        animateRows: true,
        rowGroupPanelShow: "never",
        sideBar: false,
        pagination: false,
        suppressMovableColumns: true,
        domLayout: "normal",
        rowSelection,
        // Customize the built-in selection column
        selectionColumnDef: {
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          resizable: false,
          suppressMovable: true,
          lockPosition: "left",
          cellRendererSelector: (params: ICellRendererParams<MultiProjectArea>) => {
            const isPreviouslySelected = params.data?.ID && previouslySelectedIdsRef.current.includes(params.data.ID);
            if (isPreviouslySelected) {
              // Show icon instead of checkbox for previously selected rows
              return {
                component: CompletedIndicatorRenderer,
              };
            }
            return undefined; // Use default checkbox renderer
          },
        },
      }),
      [rowSelection]
    );

    const useRankingColumns = useCallback((): ColDef[] => {
      return [...rankingColumns].toSpliced(-1, 0, {
        headerName: "Addresses",
        flex: 1,
        resizable: false,
        suppressHeaderMenuButton: true,
        valueGetter: (params: ValueGetterParams) =>
          areaStatistics?.find((areaState) => areaState.Area.ID === params.data.ID)?.AddressCounts.AddressCount ?? 0,
        valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, 0),
      });
    }, [areaStatistics]);

    const handleOnRowDataUpdated = (event: RowDataUpdatedEvent<MultiProjectArea>) => {
      event.api.forEachNode((node) => {
        if (node.data?.ID && (selectedIds.includes(node.data.ID) || previouslySelectedIds.includes(node.data.ID))) {
          node.setSelected(true);
        }
      });
    };

    const handleOnGridReady = (event: GridReadyEvent) => {
      gridApi.current = event.api;
    };

    return (
      <AgGrid
        ref={ref}
        columnDefs={useRankingColumns()}
        rowData={multiProject.Areas}
        containerProps={{
          height: 158,
        }}
        loading={loading}
        size="compact"
        variant="outlined"
        gridOptions={gridOptions}
        onGridReady={handleOnGridReady}
        onRowDataUpdated={handleOnRowDataUpdated}
        onSelectionChanged={onSelectionChanged}
      />
    );
  }
);

SubareaList.displayName = "SubareaList";
export default SubareaList;
